/**
 * Cart Delivery Info Migration Script v4
 *
 * Migrates cart items that are missing the 'Verfügbarkeit' property.
 * Uses StorefrontDeliveryHelper (Storefront API) for accurate metafield data.
 *
 * Can be removed after all users have been migrated (~30 days after deployment).
 */

(function() {
  'use strict';

  var MIGRATION_KEY = 'snowflake_cart_delivery_migrated_v2';
  var MIGRATION_IN_PROGRESS_KEY = 'snowflake_cart_delivery_migration_in_progress';
  var MAX_RETRIES = 3;
  var RETRY_DELAY = 2000;
  var HELPER_WAIT_TIMEOUT = 10000;
  var SKIP_VARIANT_ID = 32206654079059;
  var FAST_DELIVERY_TEXT = 'Auf Lager. Lieferzeit 1-4 Werktage';
  var DEFAULT_SLOW_TEXT = 'Lieferzeit ca. 4 - 6 Wochen';

  function isMigrationNeeded() {
    try {
      return localStorage.getItem(MIGRATION_KEY) !== 'true';
    } catch (e) {
      return true;
    }
  }

  function isMigrationInProgress() {
    try {
      var inProgress = localStorage.getItem(MIGRATION_IN_PROGRESS_KEY);
      if (inProgress) {
        var timestamp = parseInt(inProgress, 10);
        if (Date.now() - timestamp < 60000) {
          return true;
        }
      }
      return false;
    } catch (e) {
      return false;
    }
  }

  function setMigrationInProgress() {
    try {
      localStorage.setItem(MIGRATION_IN_PROGRESS_KEY, Date.now().toString());
    } catch (e) {}
  }

  function clearMigrationInProgress() {
    try {
      localStorage.removeItem(MIGRATION_IN_PROGRESS_KEY);
    } catch (e) {}
  }

  function setMigrationComplete() {
    try {
      localStorage.setItem(MIGRATION_KEY, 'true');
      clearMigrationInProgress();
    } catch (e) {}
  }

  function getCart() {
    return fetch('/cart.js')
      .then(function(res) { return res.json(); });
  }

  function updateCartItemByKey(itemKey, quantity, properties) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: itemKey,
        quantity: quantity,
        properties: properties
      })
    })
    .then(function(res) { return res.json(); })
    .then(function(result) {
      if (result.status) {
        throw new Error(result.description || result.message || 'Cart update failed');
      }
      return result;
    });
  }

  /**
   * Waits for StorefrontDeliveryHelper to be available
   */
  function waitForHelper() {
    return new Promise(function(resolve, reject) {
      if (window.StorefrontDeliveryHelper) {
        resolve();
        return;
      }
      var startTime = Date.now();
      var interval = setInterval(function() {
        if (window.StorefrontDeliveryHelper) {
          clearInterval(interval);
          resolve();
        } else if (Date.now() - startTime > HELPER_WAIT_TIMEOUT) {
          clearInterval(interval);
          reject(new Error('StorefrontDeliveryHelper not loaded'));
        }
      }, 200);
    });
  }

  function findItemsWithoutDeliveryInfo(cart) {
    var itemsToUpdate = [];

    for (var i = 0; i < cart.items.length; i++) {
      var item = cart.items[i];

      if (item.variant_id == SKIP_VARIANT_ID) {
        continue;
      }

      var hasDeliveryInfo = item.properties &&
                            item.properties['Verfügbarkeit'] &&
                            item.properties['Verfügbarkeit'].trim() !== '';

      if (!hasDeliveryInfo) {
        itemsToUpdate.push({
          key: item.key,
          variantId: item.variant_id,
          handle: item.handle,
          quantity: item.quantity,
          properties: item.properties || {}
        });
      }
    }

    return itemsToUpdate;
  }

  /**
   * Gets delivery info via Storefront API (replaces old HTML extraction)
   */
  function getDeliveryInfoForItem(item) {
    return window.StorefrontDeliveryHelper.getVariantDeliveryInfo(
      item.handle,
      item.variantId,
      FAST_DELIVERY_TEXT,
      DEFAULT_SLOW_TEXT
    )
    .then(function(info) {
      if (!info) return DEFAULT_SLOW_TEXT;
      return info.deliveryText || DEFAULT_SLOW_TEXT;
    })
    .catch(function() {
      return DEFAULT_SLOW_TEXT;
    });
  }

  function updateSingleItem(item) {
    return getDeliveryInfoForItem(item)
      .then(function(deliveryText) {
        if (!deliveryText) return Promise.resolve();

        var newProperties = {};
        if (item.properties) {
          Object.keys(item.properties).forEach(function(key) {
            newProperties[key] = item.properties[key];
          });
        }
        newProperties['Verfügbarkeit'] = deliveryText;

        return updateCartItemByKey(item.key, item.quantity, newProperties);
      })
      .catch(function(err) {
        return Promise.resolve();
      });
  }

  function updateAllItems(items) {
    if (items.length === 0) return Promise.resolve();

    var promise = Promise.resolve();
    items.forEach(function(item) {
      promise = promise.then(function() {
        return updateSingleItem(item);
      });
    });
    return promise;
  }

  function refreshCartView() {
    try {
      if (window.PXUTheme && window.PXUTheme.jsAjaxCart &&
          typeof window.PXUTheme.jsAjaxCart.updateView === 'function') {
        window.PXUTheme.jsAjaxCart.updateView();
      }
    } catch (e) {}
  }

  function runMigration(retryCount) {
    retryCount = retryCount || 0;

    getCart()
      .then(function(cart) {
        if (!cart.items || cart.items.length === 0) {
          setMigrationComplete();
          return;
        }

        var itemsToUpdate = findItemsWithoutDeliveryInfo(cart);

        if (itemsToUpdate.length === 0) {
          setMigrationComplete();
          return;
        }

        return waitForHelper()
          .then(function() {
            return updateAllItems(itemsToUpdate);
          })
          .then(function() {
            return getCart();
          })
          .then(function(verifyCart) {
            var remainingItems = findItemsWithoutDeliveryInfo(verifyCart);

            if (remainingItems.length === 0) {
              setMigrationComplete();
              refreshCartView();
            } else if (retryCount < MAX_RETRIES) {
              clearMigrationInProgress();
              setTimeout(function() {
                runMigration(retryCount + 1);
              }, RETRY_DELAY);
            } else {
              setMigrationComplete();
            }
          });
      })
      .catch(function(error) {
        if (retryCount < MAX_RETRIES) {
          clearMigrationInProgress();
          setTimeout(function() {
            runMigration(retryCount + 1);
          }, RETRY_DELAY);
        } else {
          setMigrationComplete();
        }
      });
  }

  function init() {
    if (!isMigrationNeeded()) return;
    if (isMigrationInProgress()) return;

    setMigrationInProgress();

    if (document.readyState === 'complete') {
      setTimeout(runMigration, 1500);
    } else {
      window.addEventListener('load', function() {
        setTimeout(runMigration, 1500);
      });
    }
  }

  init();
})();
